import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

// Guide-drag state: repositioning existing selected guides
type GuideDragState = {
  type: 'horizontal' | 'vertical'
  startPointerPos: number
  startPositions: Record<string, number>
}

/**
 * Selecting and dragging existing ruler guides. Positions are kept in local state while dragging
 * and committed through `updateGuides` on release.
 */
export function useGuideDrag({
  guidesRef,
  selectedGuideIdsRef,
  setSelectedGuideIds,
  localDragGuidesRef,
  setLocalDragGuides,
  updateGuidesRef,
  clearShapeSelectionWithHistory,
  onBackgroundClick,
  scale,
  bleedWidth,
  bleedHeight,
}: {
  guidesRef: MutableRefObject<Guide[]>
  selectedGuideIdsRef: MutableRefObject<Set<string>>
  setSelectedGuideIds: Dispatch<SetStateAction<Set<string>>>
  localDragGuidesRef: MutableRefObject<Guide[] | null>
  setLocalDragGuides: Dispatch<SetStateAction<Guide[] | null>>
  updateGuidesRef: MutableRefObject<(next: Guide[] | ((prev: Guide[]) => Guide[])) => void>
  clearShapeSelectionWithHistory: (textBoxIds: string[]) => void
  onBackgroundClick: CenterCanvasConfig['onBackgroundClick']
  scale: number
  bleedWidth: number
  bleedHeight: number
}) {
  const [guideDrag, setGuideDrag] = useState<GuideDragState | null>(null)
  const guideDragRef = useRef<GuideDragState | null>(null)

  guideDragRef.current = guideDrag

  // ── Guide pointer-down: handles selection (incl. shift-multi) and drag initiation ──
  const handleGuideDragStart = useCallback(
    (guideId: string, shiftKey: boolean, altKey: boolean, e: ReactPointerEvent) => {
      let activeSelection = selectedGuideIdsRef.current

      if (shiftKey && altKey) {
        return
      }

      if (shiftKey) {
        // Shift+click: toggle guide in/out of selection
        activeSelection = new Set(activeSelection)
        if (activeSelection.has(guideId)) activeSelection.delete(guideId)
        else activeSelection.add(guideId)
        setSelectedGuideIds(activeSelection)
      } else if (!activeSelection.has(guideId)) {
        // Normal click on unselected guide: replace selection
        activeSelection = new Set([guideId])
        setSelectedGuideIds(activeSelection)
      }

      // Mutual exclusion: deselect objects
      clearShapeSelectionWithHistory([])
      onBackgroundClick?.()

      // Only start drag if the clicked guide is in the (new) selection
      if (!activeSelection.has(guideId)) return

      const guide = guidesRef.current.find((g) => g.id === guideId)

      if (!guide) return

      const startPositions: Record<string, number> = {}

      guidesRef.current.forEach((g) => {
        if (activeSelection.has(g.id) && g.type === guide.type) {
          startPositions[g.id] = g.position
        }
      })

      const startPointerPos = guide.type === 'horizontal' ? e.clientY : e.clientX

      setGuideDrag({ type: guide.type, startPointerPos, startPositions })
    },
    [
      clearShapeSelectionWithHistory,
      onBackgroundClick,
      selectedGuideIdsRef,
      setSelectedGuideIds,
      guidesRef,
    ]
  )

  // Global pointer listeners for guide repositioning drag.
  // During drag, positions are kept in local state for smooth visuals.
  // The parent (onGuidesChange / API) is notified only on pointer up.
  useEffect(() => {
    if (!guideDrag) return

    document.body.style.userSelect = 'none'

    const onMove = (e: PointerEvent) => {
      const drag = guideDragRef.current

      if (!drag) return

      const cur = drag.type === 'horizontal' ? e.clientY : e.clientX
      const delta = (cur - drag.startPointerPos) / scale
      const maxPos = drag.type === 'horizontal' ? bleedHeight : bleedWidth

      const next = guidesRef.current.map((g) => {
        const startPos = drag.startPositions[g.id]

        if (startPos === undefined) return g
        return { ...g, position: Math.max(0, Math.min(maxPos, startPos + delta)) }
      })

      localDragGuidesRef.current = next
      setLocalDragGuides(next)
    }

    const onUp = () => {
      const final = localDragGuidesRef.current

      if (final) {
        updateGuidesRef.current(
          final.map((g) => ({ ...g, position: Math.round(g.position * 1000) / 1000 }))
        )
      }

      localDragGuidesRef.current = null
      setLocalDragGuides(null)
      setGuideDrag(null)
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      document.body.style.userSelect = ''
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [
    bleedHeight,
    bleedWidth,
    guideDrag,
    scale,
    guidesRef,
    localDragGuidesRef,
    setLocalDragGuides,
    updateGuidesRef,
  ])

  return { handleGuideDragStart }
}
